import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Calendar, AlertCircle, Lock, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AuthenticationLoginPortal = () => {
  const { user, loading, signIn } = useAuth();
  const navigate = useNavigate();
  
  const [loginState, setLoginState] = useState({
    email: '',
    password: '',
    isLoading: false,
    error: '',
    attemptCount: 0,
    isLocked: false,
    lockoutTime: null,
    showPassword: false
  });
  
  const [currentTime, setCurrentTime] = useState(new Date());

  // CREDENTIALS REMAIN FOR AUTHENTICATION - NOT DISPLAYED PUBLICLY
  const demoCredentials = [
    { role: 'Admin', email: 'secure.admin@sailorsbailbonds.com', password: 'SecureAdmin#2025!Bail' },
    { role: 'Agent', email: 'secure.agent@sailorsbailbonds.com', password: 'SecureAgent#2025!Bail' },
    { role: 'Operator', email: 'secure.operator@sailorsbailbonds.com', password: 'SecureOp#2025!Bail' },
    { role: 'Michael Sailors', email: 'michael@sailorsbail.com', password: 'X3923v62811$' }
  ];

  // Redirect if already authenticated
  useEffect(() => {
    if (user && !loading) {
      navigate('/seo-dashboard-analytics-hub');
    }
  }, [user, loading, navigate]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Handle account lockout
  useEffect(() => {
    if (loginState?.isLocked && loginState?.lockoutTime) {
      const timer = setTimeout(() => {
        setLoginState(prev => ({
          ...prev,
          isLocked: false,
          lockoutTime: null,
          attemptCount: 0,
          error: ''
        }));
      }, 300000); // 5 minutes lockout
      return () => clearTimeout(timer);
    }
  }, [loginState?.isLocked, loginState?.lockoutTime]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    
    if (loginState?.isLocked || loading) {
      return;
    }

    setLoginState(prev => ({ ...prev, isLoading: true, error: '' }));

    try {
      const result = await signIn(loginState?.email, loginState?.password);

      if (!result) {
        throw new Error('Authentication failed');
      }

      // Successful login - redirect will happen via useEffect
      setLoginState(prev => ({ 
        ...prev, 
        isLoading: false,
        attemptCount: 0,
        error: '',
        email: '',
        password: ''
      }));
    } catch (error) {
      const newAttemptCount = loginState?.attemptCount + 1;
      
      if (newAttemptCount >= 3) {
        setLoginState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Account locked due to multiple failed attempts. Please try again in 5 minutes.',
          attemptCount: newAttemptCount,
          isLocked: true,
          lockoutTime: new Date()
        }));
      } else {
        setLoginState(prev => ({
          ...prev,
          isLoading: false,
          error: error?.message || `Authentication failed. ${3 - newAttemptCount} attempts remaining.`,
          attemptCount: newAttemptCount
        }));
      }
    }
  };

  const formatDateTime = (date) => {
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg animate-pulse">
            <Shield className="text-white" size={32} />
          </div>
          <p className="text-gray-600 text-lg">Loading authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Shield className="text-white" size={32} />
              </div>
              <div className="text-left">
                <h1 className="text-4xl font-bold text-gray-900">
                  Authentication Portal
                </h1>
                <p className="text-gray-600 text-lg">
                  Secure Access to SEO Dashboard
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <Calendar size={16} />
              <span>{formatDateTime(currentTime)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Login Form Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Sign In to Dashboard
                  </h2>
                  <p className="text-gray-600">
                    Enter your credentials to access the SEO analytics dashboard
                  </p>
                </div>

                {(loginState?.isLocked || loginState?.error) && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-center space-x-2">
                      <AlertCircle size={20} className="text-red-600" />
                      <span className="font-medium text-red-800">
                        {loginState?.isLocked ? 'Account Temporarily Locked' : 'Authentication Error'}
                      </span>
                    </div>
                    <p className="text-sm text-red-700 mt-2">
                      {loginState?.error}
                    </p>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="email"
                        value={loginState?.email}
                        onChange={(e) => setLoginState(prev => ({ ...prev, email: e?.target?.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="admin@sailorsbailbonds.com"
                        required
                        disabled={loginState?.isLocked}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={loginState?.showPassword ? 'text' : 'password'}
                        value={loginState?.password}
                        onChange={(e) => setLoginState(prev => ({ ...prev, password: e?.target?.value }))}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        placeholder="Enter your password"
                        required
                        disabled={loginState?.isLocked}
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={loginState?.showPassword}
                      onChange={(e) => setLoginState(prev => ({ ...prev, showPassword: e?.target?.checked }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-700">
                      Show password
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={loginState?.isLoading || loginState?.isLocked}
                    className="w-full bg-indigo-600 text-white py-3 px-6 rounded-xl font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loginState?.isLoading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar - Security & Status Only */}
            <div className="space-y-6">
              {/* Security Features */}
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Security Features
                </h3>
                
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-3"></div>
                    <span>Two-factor authentication ready</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-3"></div>
                    <span>Account lockout after 3 failed attempts</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-3"></div>
                    <span>Encrypted data transmission</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-3"></div>
                    <span>Session timeout protection</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-1.5 mr-3"></div>
                    <span>Role-based access control</span>
                  </li>
                </ul>
              </div>

              {/* System Status */}
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  System Status
                </h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Database</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-green-600">Online</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Authentication</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-green-600">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthenticationLoginPortal;